import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import { useSocket } from '../../../contexts/SocketContext';
import OffseasonNotice from '../../Shared/OffseasonNotice';
import Toast from '../../Shared/Toast';
import styles from './TeamBattles.module.css';

interface Lobby {
  id: number;
  name: string;
  minLevel: number;
  maxLevel: number;
  maxPlayers: number;
  randomTeams: boolean;
  allowBotFill: boolean;
  currentParticipants: number;
  pve: boolean;
  matchType: string;
}

const LOBBY_FILTERS_KEY = 'teamBattle_lobbyFilters';

const loadFilters = () => {
  try {
    const saved = localStorage.getItem(LOBBY_FILTERS_KEY);
    if (saved) return JSON.parse(saved);
  } catch { /* ignore */ }
  return { showWithinLevel: false, hideFullLobbies: false, hidePvE: false, hidePvP: false };
};

const TeamBattles: React.FC = () => {
  const [lobbies, setLobbies] = useState<Lobby[]>([]);
  const [filteredLobbies, setFilteredLobbies] = useState<Lobby[]>([]);
  const [userLobbyId, setUserLobbyId] = useState<number | null>(null);
  const [userLobbyMercenaryId, setUserLobbyMercenaryId] = useState<number | null>(null);
  const [showWithinLevel, setShowWithinLevel] = useState<boolean>(() => loadFilters().showWithinLevel);
  const [hideFullLobbies, setHideFullLobbies] = useState<boolean>(() => loadFilters().hideFullLobbies);
  // hidePvE and hidePvP are mutually exclusive, selecting one clears the other.
  // Both being true would filter out ALL lobbies (hide PvE AND hide PvP = nothing left).
  const [hidePvE, setHidePvE] = useState<boolean>(() => loadFilters().hidePvE);
  const [hidePvP, setHidePvP] = useState<boolean>(() => loadFilters().hidePvP);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('error');
  const [initialLoading, setInitialLoading] = useState(true);
  const mountedRef = useRef(true);
  const navigate = useNavigate();
  const location = useLocation();
  // Suppress auto-redirect when the user explicitly navigated back from a lobby
  const suppressRedirectRef = useRef(!!(location.state as any)?.fromLobby);
  const { mercenary } = useMercenary();
  const { socket } = useSocket();

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchLobbies = async () => {
    try {
      const lobbiesResponse = await axios.get('/api/team-battle/lobbies');
      setLobbies(lobbiesResponse.data);
      const participationResponse = await axios.get('/api/team-battle/participation', { withCredentials: true });
      const regular = participationResponse.data.regular || { inLobby: false, lobbyId: null, mercenaryId: null };
      const lobbyId = regular.inLobby ? regular.lobbyId : null;
      setUserLobbyId(lobbyId);
      setUserLobbyMercenaryId(regular.inLobby ? regular.mercenaryId : null);
      // Guard: only auto-redirect if the component is still mounted and the user
      // didn't explicitly press "Back to Lobbies" (which sets fromLobby / suppressRedirect).
      if (mountedRef.current && lobbyId && !suppressRedirectRef.current) {
        navigate(`/team-battle/lobby/${lobbyId}`, { replace: true });
        return; // Don't clear initialLoading, we're navigating away
      }
    } catch (error) {
      console.error('Error fetching lobbies:', error);
    }
    if (mountedRef.current) setInitialLoading(false);
  };

  // Reset the suppress flag whenever the user navigates here via the nav link
  // (new location.key, no fromLobby state).
  useEffect(() => {
    if (!(location.state as any)?.fromLobby) {
      suppressRedirectRef.current = false;
    } else {
      suppressRedirectRef.current = true;
    }
    fetchLobbies();
  }, [mercenary?.id, location.key]);

  useEffect(() => {
    if (!socket) return;
    const handleLobbyCreated = (lobby: Lobby) => {
      // Ignore ranked lobbies, they belong on the Ranked Battle page
      if ((lobby as any).isRanked) return;
      setLobbies(prev => [...prev, lobby]);
    };
    const handleLobbyRemoved = (data: { lobbyId: number }) => {
      setLobbies(prev => prev.filter(l => l.id !== data.lobbyId));
      if (userLobbyId === data.lobbyId) setUserLobbyId(null);
    };
    const handleLobbyUpdated = (data: { lobbyId: number; currentParticipants: number }) =>
      setLobbies(prev => prev.map(l => l.id === data.lobbyId ? { ...l, currentParticipants: data.currentParticipants } : l));
    // When our own battle starts, re-fetch so the lobby disappears and
    // participation state is cleared, then navigate to the battle result.
    const handleBattleRedirect = ({ battleId, mercenaryId: battleMercId }: { battleId: number; mercenaryId?: number }) => {
      const activeMercenaryId = parseInt(localStorage.getItem('mercenaryId') || '0', 10);
      if (battleMercId && activeMercenaryId && battleMercId !== activeMercenaryId) return;
      // Do NOT call fetchLobbies() here, the component is about to unmount and
      // any in-flight participation call could cause a stale auto-redirect to the
      // destroyed lobby. Data is refreshed when the user returns to this page.
      navigate(`/battle-result/${battleId}`);
    };

    socket.on('lobby_created', handleLobbyCreated);
    socket.on('lobby_removed', handleLobbyRemoved);
    socket.on('lobby_participant_count_update', handleLobbyUpdated);
    socket.on('redirect_to_battle', handleBattleRedirect);
    return () => {
      socket.off('lobby_created', handleLobbyCreated);
      socket.off('lobby_removed', handleLobbyRemoved);
      socket.off('lobby_participant_count_update', handleLobbyUpdated);
      socket.off('redirect_to_battle', handleBattleRedirect);
    };
  }, [socket, userLobbyId]);

  // Periodic re-sync every 30 s as a belt-and-suspenders fallback in case a
  // socket event is missed (reconnect window, tab sleep, etc.).
  useEffect(() => {
    const interval = setInterval(fetchLobbies, 30_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    localStorage.setItem(LOBBY_FILTERS_KEY, JSON.stringify({ showWithinLevel, hideFullLobbies, hidePvE, hidePvP }));
  }, [showWithinLevel, hideFullLobbies, hidePvE, hidePvP]);

  const toggleHidePvE = () => {
    setHidePvE(prev => {
      const next = !prev;
      if (next) setHidePvP(false); // mutually exclusive
      return next;
    });
  };

  const toggleHidePvP = () => {
    setHidePvP(prev => {
      const next = !prev;
      if (next) setHidePvE(false); // mutually exclusive
      return next;
    });
  };

  useEffect(() => {
    let filtered = lobbies;
    if (showWithinLevel && mercenary)
      filtered = filtered.filter(l => mercenary.level >= l.minLevel && mercenary.level <= l.maxLevel);
    if (hideFullLobbies) filtered = filtered.filter(l => l.currentParticipants < l.maxPlayers);
    if (hidePvE) filtered = filtered.filter(l => !l.pve);
    if (hidePvP) filtered = filtered.filter(l => l.pve);
    setFilteredLobbies(
      [...filtered].sort((a, b) => (a.id === userLobbyId ? -1 : b.id === userLobbyId ? 1 : 0))
    );
  }, [lobbies, userLobbyId, showWithinLevel, hideFullLobbies, hidePvE, hidePvP, mercenary]);

  const handleLeaveLobby = async () => {
    try {
      await axios.post(
        `/api/team-battle/lobbies/${userLobbyId}/leave`,
        { mercenaryId: userLobbyMercenaryId },
        { withCredentials: true }
      );
      setUserLobbyId(null);
      setUserLobbyMercenaryId(null);
      await fetchLobbies();
    } catch (error: any) {
      console.error('Error leaving the lobby:', error);
      setToastType('error');
      setToastMessage(error?.response?.data?.message ?? 'Failed to leave lobby.');
    }
  };

  if (initialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', color: '#a0a0b8' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>⚔</div>
          <div>Loading lobbies...</div>
        </div>
      </div>
    );
  }

  return (
    <OffseasonNotice feature="team battles">
      <div className={styles.teamBattlesContainer}>
        {toastMessage && (
          <Toast message={toastMessage} type={toastType} duration={4000} onClose={() => setToastMessage(null)} />
        )}
        <h1 className={styles.title}>Team Battles</h1>
        <div className={styles.titleDecor}>- Choose Your War -</div>

        <div className={styles.topBar}>
          {!userLobbyId ? (
            <button className={styles.createLobbyButton} onClick={() => navigate('/team-battle/create')}>
              ＋ Create Lobby
            </button>
          ) : (
            <button className={styles.leaveLobbyButton} onClick={handleLeaveLobby}>
              ✕ Leave Current Lobby
            </button>
          )}
          <div className={styles.filterBar}>
            <span className={styles.filterLabel}>Filter:</span>
            <button className={`${styles.filterButton} ${showWithinLevel ? styles.activeFilter : ''}`}
              onClick={() => setShowWithinLevel(!showWithinLevel)}>My Level</button>
            <button className={`${styles.filterButton} ${hideFullLobbies ? styles.activeFilter : ''}`}
              onClick={() => setHideFullLobbies(!hideFullLobbies)}>Hide Full</button>
            <button className={`${styles.filterButton} ${hidePvE ? styles.activeFilter : ''}`}
              onClick={toggleHidePvE}>PvP Only</button>
            <button className={`${styles.filterButton} ${hidePvP ? styles.activeFilter : ''}`}
              onClick={toggleHidePvP}>PvE Only</button>
          </div>
        </div>

        <div className={styles.sectionLabel}>Open Lobbies - {filteredLobbies.length} available</div>

        <div className={styles.lobbiesList}>
          {filteredLobbies.length === 0 ? (
            <p className={styles.noLobbies}>No war rooms found. Be the first to raise your banner.</p>
          ) : (
            filteredLobbies.map((lobby) => {
              const isFull = lobby.currentParticipants >= lobby.maxPlayers;
              const fillPct = Math.round((lobby.currentParticipants / lobby.maxPlayers) * 100);
              return (
                <div key={lobby.id}
                  className={`${styles.lobbyCard} ${lobby.id === userLobbyId ? styles.highlight : ''}`}
                  onClick={() => navigate(`/team-battle/lobby/${lobby.id}`)}>

                  <div className={styles.lobbyCardHeader}>
                    <h2 className={styles.lobbyName}>{lobby.name}</h2>
                    <div className={styles.lobbyBadges}>
                      {lobby.id === userLobbyId && <span className={`${styles.badge} ${styles.badgeMine}`}>Yours</span>}
                      {lobby.pve
                        ? <span className={`${styles.badge} ${styles.badgePvE}`}>PvE</span>
                        : <span className={`${styles.badge} ${styles.badgePvP}`}>PvP</span>}
                      {lobby.matchType === 'death' && <span className={`${styles.badge} ${styles.badgeDeath}`}>Death</span>}
                      {lobby.randomTeams && <span className={`${styles.badge} ${styles.badgeRandom}`}>Rng</span>}
                      {lobby.allowBotFill && <span className={`${styles.badge} ${styles.badgeBot}`}>Bot Fill</span>}
                    </div>
                  </div>

                  <div className={styles.lobbyMeta}>
                    <div className={styles.metaRow}>
                      <span className={styles.metaIcon}>⚔</span>
                      <span className={styles.metaLabel}>Levels</span>
                      <span className={styles.metaValue}>{lobby.minLevel}–{lobby.maxLevel}</span>
                    </div>
                    <div className={styles.metaRow}>
                      <span className={styles.metaIcon}>⚙</span>
                      <span className={styles.metaLabel}>Teams</span>
                      <span className={styles.metaValue}>{lobby.randomTeams ? 'Random' : 'Manual'}</span>
                    </div>
                  </div>

                  <div className={isFull ? `${styles.populationBar} ${styles.populationFull}` : styles.populationBar}>
                    <div className={styles.populationFill} style={{ width: `${fillPct}%` }} />
                  </div>
                  <div className={styles.populationText}>
                    {lobby.currentParticipants} / {lobby.maxPlayers} mercenaries
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </OffseasonNotice>
  );
};

export default TeamBattles;
